/**
 * Member Support Page - Olympic Fit Wellness
 * Help center and support resources
 */
import { useState } from 'react';
import { 
  MessageCircle, 
  Phone, 
  Mail,
  ChevronDown,
  ExternalLink,
  FileText,
  HelpCircle,
  Send
} from 'lucide-react';

export default function SupportPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');

  const faqs = [
    {
      q: 'How do I book a class?',
      a: 'You can book classes through the "Book Classes" section in your member portal. Select your desired date, location, and class, then click "Book" to reserve your spot.'
    },
    {
      q: 'How do I cancel a class booking?',
      a: 'Go to "My Schedule" in your portal and find the class you want to cancel. Click the "Cancel" button next to it. Please cancel at least 4 hours before the class to avoid any penalties.'
    },
    {
      q: 'How do I freeze my membership?',
      a: 'Navigate to "My Membership" and select "Freeze Membership". You can freeze your membership for up to 3 months per year at no additional cost.'
    },
    {
      q: 'How do I update my payment method?',
      a: 'Go to "My Membership" and click on "Update" next to your current payment method. You can add a new card or update your existing payment information.'
    },
    {
      q: 'Can I bring a guest?',
      a: 'Guest passes are included with Wellness and Elite memberships. Check your membership details for the number of guest passes available to you each month.'
    }
  ];

  const handleSubmitTicket = (e) => {
    e.preventDefault();
    // TODO: Implement ticket submission
    console.log('Submitting ticket:', { ticketSubject, ticketMessage });
    setTicketSubject('');
    setTicketMessage('');
  };

  return (
    <div className="space-y-6" data-testid="support-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-neutral-900">Support</h1>
        <p className="text-neutral-500">Get help with your membership and services</p>
      </div>

      {/* Contact Options */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-[#3B82F6]/10 flex items-center justify-center mx-auto mb-4">
            <Phone className="w-7 h-7 text-[#3B82F6]" />
          </div>
          <h3 className="font-semibold text-neutral-900 mb-2">Call Us</h3>
          <p className="text-sm text-neutral-500 mb-4">Mon-Fri 6AM-10PM, Sat-Sun 8AM-8PM</p>
          <a href="tel:+14695550100" className="text-[#3B82F6] font-medium hover:underline">
            (469) 555-0100
          </a>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-7 h-7 text-green-600" />
          </div>
          <h3 className="font-semibold text-neutral-900 mb-2">Live Chat</h3>
          <p className="text-sm text-neutral-500 mb-4">Chat with our support team in real-time</p>
          <button className="text-green-600 font-medium hover:underline flex items-center justify-center gap-1 mx-auto">
            Start Chat <ExternalLink className="w-4 h-4" />
          </button>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-7 h-7 text-purple-600" />
          </div>
          <h3 className="font-semibold text-neutral-900 mb-2">Email Support</h3>
          <p className="text-sm text-neutral-500 mb-4">We'll respond within 24 hours</p>
          <a href="mailto:support@olympicfitwellness.com" className="text-purple-600 font-medium hover:underline">
            support@olympicfitwellness.com
          </a>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* FAQ Section */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b border-neutral-100">
            <h2 className="text-xl font-heading font-bold text-neutral-900 flex items-center gap-2">
              <HelpCircle className="w-5 h-5" />
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="divide-y divide-neutral-100">
            {faqs.map((faq, idx) => (
              <div key={idx}>
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-neutral-50 transition-colors"
                >
                  <span className="font-medium text-neutral-900 pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-neutral-400 flex-shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-4">
                    <p className="text-neutral-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit Ticket */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b border-neutral-100">
            <h2 className="text-xl font-heading font-bold text-neutral-900 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Submit a Support Ticket
            </h2>
          </div>
          
          <form onSubmit={handleSubmitTicket} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Subject</label>
              <input
                type="text"
                value={ticketSubject}
                onChange={(e) => setTicketSubject(e.target.value)}
                placeholder="Brief description of your issue"
                className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Category</label>
              <select className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6]">
                <option>Membership</option>
                <option>Billing</option>
                <option>Classes & Booking</option>
                <option>Facilities</option>
                <option>App & Technical</option>
                <option>Other</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Message</label>
              <textarea
                value={ticketMessage}
                onChange={(e) => setTicketMessage(e.target.value)}
                placeholder="Describe your issue in detail..."
                rows={5}
                className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:border-[#3B82F6] resize-none"
                required
              />
            </div>
            
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#3B82F6] text-white font-semibold px-6 py-3 text-sm hover:bg-blue-600 transition-colors"
            >
              <Send className="w-4 h-4" />
              Submit Ticket
            </button>
          </form>
        </div>
      </div>

      {/* Resources */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-heading font-bold text-neutral-900 mb-6">Helpful Resources</h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { title: 'Member Guide', desc: 'Everything you need to know' },
            { title: 'Class Descriptions', desc: 'Learn about our classes' },
            { title: 'Equipment Guide', desc: 'How to use gym equipment' },
            { title: 'Nutrition Tips', desc: 'Healthy eating advice' }
          ].map((resource, idx) => (
            <button
              key={idx}
              className="p-4 border border-neutral-200 rounded-lg text-left hover:border-[#3B82F6] hover:shadow-sm transition-all"
            >
              <FileText className="w-6 h-6 text-[#3B82F6] mb-2" />
              <h4 className="font-medium text-neutral-900">{resource.title}</h4>
              <p className="text-sm text-neutral-500">{resource.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
